/**
 * OutputList — Displays output channels for an expanded escalation.
 *
 * Shows/hides channels based on campus output feature flags.
 * Each output shows: enabled/disabled badge, output name, entity list, repeat interval.
 *
 * Text/Voice/Email user outputs (4/5/6) are paired with their emergency contact
 * counterparts (17/18/19) into a single display row — matching the drawer UX.
 *
 * Props:
 *   outputs         — array of output objects from escalation
 *   campusSettings  — campus settings object with output feature flags
 */

// Output type ID → campus setting key mapping
const OUTPUT_FLAG_MAP = {
    3: 'app_active',            // Mobile
    4: 'text_messaging_active', // Text (Users)
    5: 'voice_active',          // Voice (Users)
    6: 'email_active',          // Email (Users)
    7: 'pager_active',          // Pager
    9: 'offsite_active',        // Offsite
    17: 'text_messaging_active', // Text (Emergency Contacts)
    18: 'voice_active',          // Voice (Emergency Contacts)
    19: 'email_active',          // Email (Emergency Contacts)
};

// Paired outputs: user output → emergency contact output
const PAIRED_EC_IDS = { 4: 17, 5: 18, 6: 19 };
const EC_OUTPUT_IDS = new Set([17, 18, 19]);

const OutputList = ({ outputs = [], campusSettings = {} }) => {
    // Build a lookup map by output_type_id
    const outputMap = {};
    outputs.forEach((o) => { outputMap[o.output_type_id] = o; });

    // Determine which outputs are visible based on campus flags
    const visibleOutputs = outputs.filter((o) => {
        const flag = OUTPUT_FLAG_MAP[o.output_type_id];
        if (!flag || !campusSettings[flag]) return false;
        // Skip emergency contact outputs — they'll be merged into their paired user output
        if (EC_OUTPUT_IDS.has(o.output_type_id)) return false;
        return true;
    });

    if (visibleOutputs.length === 0) {
        return <p className="cf-output-empty">No outputs configured</p>;
    }

    return (
        <ul className="cf-output-list">
            {visibleOutputs.map((output) => {
                // Check if this output has a paired emergency contact output
                const ecOutputTypeId = PAIRED_EC_IDS[output.output_type_id];
                const ecOutput = ecOutputTypeId ? outputMap[ecOutputTypeId] : null;

                // Combine user entities + EC entities for display
                const userEntities = output.entities || [];
                const ecEntities = ecOutput?.entities || [];
                const hasUserEntities = userEntities.length > 0;
                const hasEcEntities = ecEntities.length > 0;
                const hasAnyEntities = hasUserEntities || hasEcEntities;

                // For paired outputs, consider either being enabled as "enabled"
                const isEnabled = ecOutput
                    ? (output.enabled || ecOutput.enabled)
                    : output.enabled;

                const entWarning = isEnabled && !hasAnyEntities && output.output_type_id !== 9;

                let badgeClass = 'cf-output-badge--disabled';
                if (entWarning) badgeClass = 'cf-output-badge--warning';
                else if (isEnabled) badgeClass = 'cf-output-badge--enabled';

                return (
                    <li key={output.output_id} className="cf-output-item">
                        <span className={`cf-output-badge ${badgeClass}`}>
                            {isEnabled ? 'Enabled' : 'Disabled'}
                        </span>
                        <span className="cf-output-name">{output.output}</span>
                        {hasUserEntities ? (
                            <span className="cf-output-entities">
                                ({userEntities.map((ref) => ref.reference_display).join(', ')})
                            </span>
                        ) : null}
                        {hasEcEntities ? (
                            <span className="cf-output-entities cf-output-entities--ec">
                                {hasUserEntities ? ' ' : ''}
                                ({ecEntities.map((ref) => ref.reference_display).join(', ')})
                            </span>
                        ) : null}
                        {isEnabled ? (
                            <span className="cf-output-repeat">
                                {'\u2022'} {output.repeat_interval > 0
                                    ? `Repeat every ${Math.floor(output.repeat_interval / 60)} min`
                                    : 'Sent once'}
                            </span>
                        ) : null}
                    </li>
                );
            })}
        </ul>
    );
};

export default OutputList;
